import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../services/api';

// Google Analytics 4 integration
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_ID;

// Initialize GA4
export function initGA() {
  if (!GA_MEASUREMENT_ID) return;
  
  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  
  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    page_path: window.location.pathname,
    send_page_view: false // We'll send manually
  });
}

// Track page view in GA4
export function trackGAPageView(path, title) {
  if (window.gtag && GA_MEASUREMENT_ID) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_title: title
    });
  }
}

// Track custom events in GA4
export function trackGAEvent(eventName, params = {}) {
  if (window.gtag) {
    window.gtag('event', eventName, params);
  }
}

// React hook for automatic page view tracking
export function usePageTracking() {
  const location = useLocation();
  
  useEffect(() => {
    const path = location.pathname + location.search;
    const referrer = document.referrer || null;
    
    // Track in our internal analytics
    trackPageView(path, referrer).catch(() => {});
    
    // Track in Google Analytics
    trackGAPageView(path, document.title);
    
  }, [location]);
}

// Analytics events for specific actions
export const AnalyticsEvents = {
  // User actions
  signUp: () => trackGAEvent('sign_up', { method: 'email' }),
  login: () => trackGAEvent('login', { method: 'email' }),
  
  // Subscription events
  viewPricing: () => trackGAEvent('view_item_list', { item_list_name: 'pricing' }),
  beginCheckout: (tier, price) => trackGAEvent('begin_checkout', { 
    currency: 'USD', 
    value: price,
    items: [{ item_name: tier, price }]
  }),
  purchase: (tier, price) => trackGAEvent('purchase', { 
    currency: 'USD', 
    value: price,
    items: [{ item_name: tier, price }]
  }),
  
  // Content engagement
  viewComic: (comicId, comicTitle) => trackGAEvent('view_item', { 
    items: [{ item_id: comicId, item_name: comicTitle }]
  }),
  readEpisode: (comicId, episodeNum) => trackGAEvent('select_content', { 
    content_type: 'episode',
    item_id: comicId,
    episode: episodeNum
  }),
  likeComic: (comicId) => trackGAEvent('add_to_wishlist', { 
    items: [{ item_id: comicId }]
  }),
  favoriteComic: (comicId) => trackGAEvent('add_to_cart', { 
    items: [{ item_id: comicId }]
  }),
  
  // Creator actions
  createComic: () => trackGAEvent('create_content', { content_type: 'comic' }),
  useAITool: (toolName) => trackGAEvent('use_feature', { feature_name: toolName }),
  
  // Search
  search: (query) => trackGAEvent('search', { search_term: query }),
  
  // Donations
  setupDonation: (amount) => trackGAEvent('donate', { value: amount, currency: 'USD' })
};

export default usePageTracking;
